/**
 * Constantes de domínio compartilhadas entre API, worker e web.
 *
 * Este arquivo não contém valores visuais. Design tokens vivem em
 * `packages/ui/src/tokens` e dependem da extração do Figma.
 */

/**
 * Timezone operacional padrão da Dream Vision.
 *
 * America/Fortaleza é UTC-3 fixo: o Ceará não observa horário de verão, e o
 * Brasil o extinguiu em 2019. Ainda assim, nada no código assume offset fixo —
 * entregas podem declarar outro IANA timezone e a conversão é sempre delegada
 * ao Luxon.
 */
export const DEFAULT_TIMEZONE = "America/Fortaleza" as const;

/** Único domínio autorizado a entrar no painel via Google. */
export const TEAM_EMAIL_DOMAIN = "dreamvision.com.br" as const;

export function isTeamEmail(email: string): boolean {
  return email.trim().toLowerCase().endsWith(`@${TEAM_EMAIL_DOMAIN}`);
}

/**
 * Encaminha o OAuth pelo seletor de contas do Workspace.
 *
 * O parâmetro `hd` no authorize é só um hint: se o Chrome já está logado no
 * Gmail pessoal, o Google autoriza essa sessão e o usuário vê Error 400.
 * O AccountChooser com o mesmo `hd` lista contas `@domínio` e esconde Gmail.
 */
export function withGoogleHostedDomainChooser(
  authorizeUrl: string,
  domain: string = TEAM_EMAIL_DOMAIN,
): string {
  let parsed: URL;
  try {
    parsed = new URL(authorizeUrl);
  } catch {
    return authorizeUrl;
  }
  if (parsed.hostname !== "accounts.google.com") return authorizeUrl;
  if (parsed.pathname.toLowerCase().includes("accountchooser")) return authorizeUrl;

  const chooser = new URL("https://accounts.google.com/AccountChooser");
  chooser.searchParams.set("hd", domain);
  chooser.searchParams.set("continue", authorizeUrl);
  return chooser.toString();
}

export const DELIVERABLE_STATUSES = [
  "planned",
  "in_production",
  "in_review",
  "approved",
  "scheduled",
  "published",
  "completed",
  "late",
  "cancelled",
] as const;

export type DeliverableStatus = (typeof DELIVERABLE_STATUSES)[number];

/** Rótulos pt-BR. O briefing exige que tipos, cores e status sejam customizáveis
 *  no futuro; por isso os status vivem em tabela e estes são apenas os padrões. */
export const DELIVERABLE_STATUS_LABELS: Record<DeliverableStatus, string> = {
  planned: "Planejado",
  in_production: "Em produção",
  in_review: "Em revisão",
  approved: "Aprovado",
  scheduled: "Agendado",
  published: "Publicado",
  completed: "Concluído",
  late: "Atrasado",
  cancelled: "Cancelado",
};

/** Status que representam trabalho encerrado — não entram no cálculo de atraso. */
export const TERMINAL_STATUSES: readonly DeliverableStatus[] = [
  "published",
  "completed",
  "cancelled",
] as const;

export const DELIVERABLE_PRIORITIES = ["low", "medium", "high", "urgent"] as const;
export type DeliverablePriority = (typeof DELIVERABLE_PRIORITIES)[number];

export const DELIVERABLE_PRIORITY_LABELS: Record<DeliverablePriority, string> = {
  low: "Baixa",
  medium: "Média",
  high: "Alta",
  urgent: "Urgente",
};

/**
 * Tipos de entrega padrão, cobrindo o escopo de "entrega" definido no briefing.
 * Persistidos em `deliverable_types` para permitir customização por organização.
 *
 * `icon` usa a chave filled do calendário (não nome de lucide). O seed sincroniza
 * slugs ausentes e atualiza ícone/rótulo dos padrões.
 */
export const DEFAULT_DELIVERABLE_TYPES = [
  {
    slug: "social_post",
    label: "Postagem em rede social",
    color: "#D0E5FF",
    icon: "social_post",
  },
  { slug: "reels", label: "Vídeo / Reels", color: "#F9DCD3", icon: "reels" },
  { slug: "logo", label: "Logomarca", color: "#E6DBF4", icon: "logo" },
  {
    slug: "delivery",
    label: "Entrega de material",
    color: "#DBECDD",
    icon: "delivery",
  },
  { slug: "content", label: "Conteúdo", color: "#E6DBF4", icon: "content" },
  { slug: "script", label: "Roteiro", color: "#F9DCD3", icon: "script" },
  { slug: "shooting", label: "Captação", color: "#DBECDD", icon: "shooting" },
  { slug: "editing", label: "Edição", color: "#BFDEDD", icon: "editing" },
  { slug: "creative", label: "Criativo", color: "#F9DCD3", icon: "creative" },
  { slug: "campaign", label: "Campanha", color: "#F9CE7A", icon: "campaign" },
  { slug: "meeting", label: "Reunião", color: "#E6DBF4", icon: "meeting" },
  { slug: "approval", label: "Aprovação", color: "#DBECDD", icon: "approval" },
  { slug: "publication", label: "Publicação", color: "#D0E5FF", icon: "publication" },
  { slug: "report", label: "Relatório", color: "#BFDEDD", icon: "report" },
  {
    slug: "admin",
    label: "Demanda administrativa",
    color: "#E7E7EC",
    icon: "admin",
  },
  {
    slug: "milestone",
    label: "Marco de projeto",
    color: "#E6DBF4",
    icon: "milestone",
  },
  { slug: "deadline", label: "Prazo", color: "#F99696", icon: "deadline" },
  { slug: "other", label: "Outra atividade", color: "#D5D8DE", icon: "other" },
] as const;

/** Tipos com data de publicação relevante (postagem, reels, campanha…). */
export const PUBLISHABLE_TYPE_SLUGS: readonly string[] = [
  "social_post",
  "reels",
  "publication",
  "campaign",
  "content",
];

export const USER_ROLES = ["admin", "client_viewer"] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const CALENDAR_VIEWS = ["mes", "semana", "dia", "agenda", "linha"] as const;
export type CalendarView = (typeof CALENDAR_VIEWS)[number];

export const CALENDAR_VIEW_LABELS: Record<CalendarView, string> = {
  mes: "Mês",
  semana: "Semana",
  dia: "Dia",
  agenda: "Agenda",
  linha: "Linha",
};

const CALENDAR_VIEW_ALIASES: Record<string, CalendarView> = {
  mes: "mes",
  month: "mes",
  semana: "semana",
  week: "semana",
  dia: "dia",
  day: "dia",
  agenda: "agenda",
  linha: "linha",
  timeline: "linha",
};

/** Aceita o slug novo e os nomes antigos em inglês na URL e nas prefs. */
export function parseCalendarView(raw: unknown): CalendarView {
  if (typeof raw !== "string") return "mes";
  const key = raw.trim().toLowerCase();
  return CALENDAR_VIEW_ALIASES[key] ?? "mes";
}

export const GROUP_BY_OPTIONS = ["none", "client", "project", "assignee"] as const;
export type GroupByOption = (typeof GROUP_BY_OPTIONS)[number];

/** Limites de upload. Validados no servidor, nunca só no cliente. */
export const UPLOAD_LIMITS = {
  maxFileSizeBytes: 25 * 1024 * 1024,
  maxFilesPerDeliverable: 20,
  allowedMimeTypes: [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/gif",
    "image/svg+xml",
    "video/mp4",
    "video/quicktime",
    "video/webm",
    "audio/mpeg",
    "audio/mp4",
    "application/pdf",
    "application/zip",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
    "text/csv",
  ],
} as const;

export const RATE_LIMITS = {
  login: { windowMs: 15 * 60 * 1000, max: 10 },
  publicShare: { windowMs: 60 * 1000, max: 60 },
  publicSharePassword: { windowMs: 15 * 60 * 1000, max: 10 },
  mutation: { windowMs: 60 * 1000, max: 300 },
  /** Turns do Moon (LLM + PDF + Figma + STT). Mais caro que uma mutação comum. */
  hermes: { windowMs: 15 * 60 * 1000, max: 40 },
} as const;

export const HERMES_LIMITS = {
  maxPdfBytes: 8 * 1024 * 1024,
  maxAudioBytes: 12 * 1024 * 1024,
  maxMessages: 30,
  maxMessageChars: 20_000,
  maxPdfChars: 80_000,
  maxPdfPages: 8,
  maxToolRounds: 12,
  maxBatchItems: 40,
  maxPresentationImages: 12,
  maxPresentationImageBytes: 8 * 1024 * 1024,
} as const;

/** Teto de expansão de recorrência por consulta. Impede que uma RRULE sem UNTIL
 *  gere trabalho ilimitado ao expandir um intervalo muito largo. */
export const MAX_OCCURRENCES_PER_QUERY = 2000;

/** Janela máxima consultável do calendário, em dias. */
export const MAX_CALENDAR_RANGE_DAYS = 400;

export const OUTBOX = {
  batchSize: 100,
  pollIntervalMs: 1000,
  maxAttempts: 12,
  notifyChannel: "dream_calendar_events",
} as const;

/**
 * Identificadores estáveis das 34 redes do postador.
 *
 * Os nomes batem com a API pública do Postiz (`__type`) para quem já opera
 * aquele vocabulário. O código das redes é implementação própria: Postiz é
 * AGPL-3.0 e Next/Prisma, incompatível com este repositório.
 */
export const SOCIAL_PLATFORM_IDS = [
  "x",
  "linkedin",
  "linkedin-page",
  "reddit",
  "instagram",
  "instagram-standalone",
  "facebook",
  "threads",
  "youtube",
  "gmb",
  "tiktok",
  "pinterest",
  "dribbble",
  "discord",
  "slack",
  "kick",
  "twitch",
  "mastodon",
  "bluesky",
  "lemmy",
  "wrapcast",
  "telegram",
  "nostr",
  "vk",
  "medium",
  "devto",
  "hashnode",
  "wordpress",
  "listmonk",
  "moltbook",
  "whop",
  "skool",
  "mewe",
  "tumblr",
] as const;

export type SocialPlatformId = (typeof SOCIAL_PLATFORM_IDS)[number];

export const SOCIAL_ACCOUNT_STATUSES = ["active", "pending", "error", "disconnected"] as const;
export type SocialAccountStatus = (typeof SOCIAL_ACCOUNT_STATUSES)[number];

export const SOCIAL_AUTH_METHODS = ["sandbox", "oauth", "credentials", "webhook", "bot"] as const;
export type SocialAuthMethod = (typeof SOCIAL_AUTH_METHODS)[number];

export const SOCIAL_POST_STATUSES = [
  "draft",
  "scheduled",
  "publishing",
  "published",
  "partial",
  "failed",
  "cancelled",
] as const;
export type SocialPostStatus = (typeof SOCIAL_POST_STATUSES)[number];

export const SOCIAL_TARGET_STATUSES = [
  "pending",
  "publishing",
  "published",
  "failed",
  "skipped",
] as const;
export type SocialTargetStatus = (typeof SOCIAL_TARGET_STATUSES)[number];

export const SOCIAL_POST_STATUS_LABELS: Record<SocialPostStatus, string> = {
  draft: "Rascunho",
  scheduled: "Agendado",
  publishing: "Publicando",
  published: "Publicado",
  partial: "Parcial",
  failed: "Falhou",
  cancelled: "Cancelado",
};
